import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import bakingApi from '../services/bakingApi';
import { useAuth } from '../context/AuthContext';
import {
  NODE_CATEGORIES,
  PICKER_NODE_TYPES,
  PORT_TYPES,
  assembledMaterials,
  assembledText,
  canExecuteCatalogNode,
  connect,
  connectedInputMaterials,
  connectedInputText,
  defaultPortsForType,
  downstreamApprovalsInvalidated,
  formatUsageSummary,
  formatWorkflowTotals,
  getNodeDef,
  imageInputLocked,
  isExecutableNode,
  localNodeEstimate,
  mergeRunHistory,
  newNode,
  nodeExecutionState,
  retainedSuccessPreview,
  statusLabel,
} from '../features/productionWorkflow';
import CostEstimateTooltip from '../features/CostEstimateTooltip';
import CatalogNodePicker from '../features/CatalogNodePicker';
import FirstCharacterTutorial from '../studio/FirstCharacterTutorial';
import './production.css';
import {
  ACCEPTED_IMAGE_TYPES,
  MAX_IMAGE_BYTES,
  imageNodePrompt,
  replaceProjectImage,
  uploadProjectImage,
  validateImageFile,
} from '../studio/projectImages';

export default function ProductionWorkflowPage() {
  const { projectId, recordId, department } = useParams();
  return (
    <Workflow
      key={`${projectId}/${department}/${recordId}`}
      projectId={projectId}
      recordId={recordId}
      department={department}
    />
  );
}

function Workflow({ projectId, recordId, department }) {
  const { hasTenantPermission, hasPlatformPermission } = useAuth();
  const canEdit = hasTenantPermission('project.update');
  const canUseDevWorker = hasPlatformPermission('platform.dev_worker');
  const [record, setRecord] = useState(null);
  const [records, setRecords] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [source, setSource] = useState(null);
  const [contextMenu, setContextMenu] = useState(null);
  const [tutorialOpen, setTutorialOpen] = useState(false);
  const [error, setError] = useState('');
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [zoom, setZoom] = useState(0.75);
  const [notice, setNotice] = useState('');
  const [pickerQuery, setPickerQuery] = useState('');
  const [runsByNode, setRunsByNode] = useState({});
  const [usageTotals, setUsageTotals] = useState(null);
  const [runningNodeId, setRunningNodeId] = useState('');
  const [historyPages, setHistoryPages] = useState({});
  const [historyLoading, setHistoryLoading] = useState('');
  const [executionPlan, setExecutionPlan] = useState(null);
  const [planLoading, setPlanLoading] = useState(false);
  const [runAllBusy, setRunAllBusy] = useState(false);
  const [nodeEstimates, setNodeEstimates] = useState({});
  const [projectImages, setProjectImages] = useState([]);
  const [replaceProgress, setReplaceProgress] = useState({});
  const [replaceMenuNodeId, setReplaceMenuNodeId] = useState('');
  const drag = useRef(null);
  const uploadSequence = useRef(0);
  const pollRef = useRef(null);
  const runAllIdempotency = useRef('');

  const applyRunPayload = useCallback((data, { replaceHistory = false, nodeId = null } = {}) => {
    setRunsByNode((prev) => {
      const next = { ...prev };
      const summaries = data.node_summaries || {};
      Object.entries(summaries).forEach(([id, summary]) => {
        const existing = next[id] || { history: [] };
        next[id] = {
          ...existing,
          // Authoritative summaries independent of which history page is loaded.
          active_run: summary.active_run || null,
          latest_attempt: summary.latest_attempt || null,
          latest_success: summary.latest_success || null,
          history: replaceHistory && (!nodeId || nodeId === id) ? [] : existing.history || [],
        };
      });
      for (const run of data.runs || []) {
        const bucket = next[run.node_id] || {
          history: [],
          active_run: null,
          latest_attempt: null,
          latest_success: null,
        };
        const shouldReplace =
          replaceHistory && (!nodeId || nodeId === run.node_id) && !(bucket.history || []).length;
        bucket.history = mergeRunHistory(shouldReplace ? [] : bucket.history || [], [run]);
        // Do not override authoritative node_summaries when present.
        if (!summaries[run.node_id]) {
          if (!bucket.latest_attempt) bucket.latest_attempt = run;
          if (['queued', 'running'].includes(run.status) && !bucket.active_run) {
            bucket.active_run = run;
          }
          if (run.status === 'succeeded' && !bucket.latest_success) {
            bucket.latest_success = run;
          }
        }
        next[run.node_id] = bucket;
      }
      return next;
    });
    setUsageTotals(data.usage_totals || null);
    if (nodeId) {
      setHistoryPages((prev) => ({
        ...prev,
        [nodeId]: {
          page: data.page || 1,
          hasMore: Boolean(data.has_more),
          totalCount: data.total_count || 0,
          scoped: true,
        },
      }));
    }
    return data;
  }, []);

  const loadRuns = useCallback(async () => {
    const data = await bakingApi.listProductionNodeRuns(projectId, recordId, {
      page: 1,
      pageSize: 50,
    });
    return applyRunPayload(data, { replaceHistory: false });
  }, [projectId, recordId, applyRunPayload]);

  const loadMoreHistory = useCallback(
    async (nodeId) => {
      if (!nodeId || historyLoading) return;
      const meta = historyPages[nodeId] || { page: 0, hasMore: true, scoped: false };
      if (meta.scoped && meta.hasMore === false) return;
      setHistoryLoading(nodeId);
      try {
        const nextPage = meta.scoped ? (meta.page || 1) + 1 : 1;
        const data = await bakingApi.listProductionNodeRuns(projectId, recordId, {
          nodeId,
          page: nextPage,
          pageSize: 20,
        });
        applyRunPayload(data, { nodeId });
      } catch (e) {
        setError(e.message || 'Could not load run history.');
      } finally {
        setHistoryLoading('');
      }
    },
    [historyLoading, historyPages, projectId, recordId, applyRunPayload]
  );
  useEffect(() => {
    let live = true;
    Promise.all([
      bakingApi.getProductionRecord(projectId, recordId),
      bakingApi.listProductionRecords(projectId),
      bakingApi.listProductionNodeRuns(projectId, recordId).catch(() => ({ runs: [], node_summaries: {} })),
    ])
      .then(([data, rows, runPayload]) => {
        if (!live) return;
        if (data.department !== department) {
          setError('Record not found in this department.');
          return;
        }
        setRecord(data);
        setRecords(rows);
        setSelectedId(data.graph.nodes[0]?.id || null);
        applyRunPayload(runPayload);
      })
      .catch((e) => {
        if (live) setError(e.message);
      });
    return () => {
      live = false;
      uploadSequence.current += 1;
    };
  }, [projectId, recordId, department, applyRunPayload]);

  useEffect(() => {
    if (!dirty) return undefined;
    const unload = (event) => {
      event.preventDefault();
      event.returnValue = '';
    };
    const navigate = (event) => {
      const link = event.target.closest('a[href]');
      if (
        link &&
        !link.hasAttribute('download') &&
        !window.confirm('You have unsaved workflow changes. Leave without saving?')
      ) {
        event.preventDefault();
        event.stopPropagation();
      }
    };
    window.addEventListener('beforeunload', unload);
    document.addEventListener('click', navigate, true);
    return () => {
      window.removeEventListener('beforeunload', unload);
      document.removeEventListener('click', navigate, true);
    };
  }, [dirty]);

  const hasActiveRuns = useMemo(
    () => Object.values(runsByNode).some((s) => s.active_run),
    [runsByNode]
  );

  useEffect(() => {
    if (!hasActiveRuns) {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
      return undefined;
    }
    pollRef.current = setInterval(() => {
      loadRuns().catch(() => {});
    }, 2000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [hasActiveRuns, loadRuns]);

  const change = (patch) => {
    if (!canEdit || busy) return;
    setRecord((r) => ({ ...r, ...patch }));
    setDirty(true);
    setNotice('');
  };
  const graph = record?.graph || { nodes: [], edges: [] };
  const selected = graph.nodes.find((n) => n.id === selectedId);
  const selectedSummary = selected ? runsByNode[selected.id] : null;
  const updateNode = (id, patch) =>
    change({
      graph: {
        ...graph,
        nodes: graph.nodes.map((n) => (n.id === id ? { ...n, ...patch } : n)),
      },
    });
  const add = (type, position = null) => {
    const node = newNode(type, graph.nodes.length, position);
    change({ graph: { ...graph, nodes: [...graph.nodes, node] } });
    setSelectedId(node.id);
    setPickerQuery('');
    setContextMenu(null);
    return node;
  };
  const join = (target, targetPort = 'in') => {
    if (!source?.nodeId) return;
    try {
      change({
        graph: connect(
          graph,
          source.nodeId,
          target,
          source.portId || 'out',
          targetPort
        ),
      });
      setSource(null);
      setError('');
    } catch (e) {
      setError(e.message);
    }
  };
  const save = async () => {
    setBusy(true);
    setError('');
    try {
      const saved = await bakingApi.saveProductionRecord(projectId, recordId, record);
      setRecord(saved);
      setDirty(false);
      setNotice('All changes saved.');
      await loadRuns();
    } catch (e) {
      setError(e.details ? `${e.message} ${Object.values(e.details).flat().join(' ')}` : e.message);
    } finally {
      setBusy(false);
    }
  };
  const upload = async (file, id) => {
    if (!file) return;
    const validation = validateImageFile(file);
    if (validation) {
      setError(validation);
      return;
    }
    if (imageInputLocked(graph, id)) {
      const locked = downstreamApprovalsInvalidated(graph, [id])
        .map((nid) => graph.nodes.find((n) => n.id === nid)?.label || nid)
        .join(', ');
      setError(
        `Reopen approved downstream work before replacing this image: ${locked}.`
      );
      return;
    }
    const sequence = ++uploadSequence.current;
    const previous = record.graph.nodes.find((n) => n.id === id);
    setBusy(true);
    setError('');
    setReplaceProgress((p) => ({ ...p, [id]: 0 }));
    try {
      const image = await uploadProjectImage(bakingApi, projectId, file, {
        onProgress: (pct) => setReplaceProgress((p) => ({ ...p, [id]: pct })),
      });
      if (sequence !== uploadSequence.current) return;
      setRecord((r) => ({
        ...r,
        graph: {
          ...r.graph,
          nodes: r.graph.nodes.map((n) =>
            n.id === id
              ? {
                  ...n,
                  file_id: image.id,
                  image: '',
                  original_filename: image.original_filename || file.name,
                  prompt: imageNodePrompt(n) || n.prompt || n.text || '',
                  text: imageNodePrompt(n) || n.prompt || n.text || '',
                }
              : n
          ),
        },
      }));
      setDirty(true);
      setNotice('Image uploaded to project storage. Previous shared file was not deleted.');
      setReplaceMenuNodeId('');
      refreshExecutionPlan();
    } catch (e) {
      if (previous) {
        setRecord((r) => ({
          ...r,
          graph: {
            ...r.graph,
            nodes: r.graph.nodes.map((n) => (n.id === id ? previous : n)),
          },
        }));
      }
      setError(e.message || 'Upload failed.');
    } finally {
      if (sequence === uploadSequence.current) setBusy(false);
      setReplaceProgress((p) => {
        const next = { ...p };
        delete next[id];
        return next;
      });
    }
  };

  const replaceExistingFile = async (id, file) => {
    if (!file) return;
    const node = graph.nodes.find((n) => n.id === id);
    if (!node?.file_id) {
      await upload(file, id);
      return;
    }
    if (imageInputLocked(graph, id)) {
      const locked = downstreamApprovalsInvalidated(graph, [id])
        .map((nid) => graph.nodes.find((n) => n.id === nid)?.label || nid)
        .join(', ');
      setError(
        `Reopen approved downstream work before replacing this image: ${locked}.`
      );
      return;
    }
    const validation = validateImageFile(file);
    if (validation) {
      setError(validation);
      return;
    }
    const previous = { ...node };
    setBusy(true);
    setError('');
    setReplaceProgress((p) => ({ ...p, [id]: 0 }));
    try {
      const image = await replaceProjectImage(bakingApi, projectId, node.file_id, file, {
        onProgress: (pct) => setReplaceProgress((p) => ({ ...p, [id]: pct })),
      });
      setRecord((r) => ({
        ...r,
        graph: {
          ...r.graph,
          nodes: r.graph.nodes.map((n) =>
            n.id === id
              ? {
                  ...n,
                  file_id: image.id || n.file_id,
                  image: '',
                  original_filename: image.original_filename || file.name,
                }
              : n
          ),
        },
      }));
      setDirty(true);
      setNotice('Image replaced. Node identity, connections, and instructions were kept.');
      setReplaceMenuNodeId('');
      refreshExecutionPlan();
    } catch (e) {
      setRecord((r) => ({
        ...r,
        graph: {
          ...r.graph,
          nodes: r.graph.nodes.map((n) => (n.id === id ? previous : n)),
        },
      }));
      setError(e.message || 'Replace failed. Original image kept.');
    } finally {
      setBusy(false);
      setReplaceProgress((p) => {
        const next = { ...p };
        delete next[id];
        return next;
      });
    }
  };

  const attachProjectImage = (id, imageId) => {
    if (!imageId) return;
    if (imageInputLocked(graph, id)) {
      const locked = downstreamApprovalsInvalidated(graph, [id])
        .map((nid) => graph.nodes.find((n) => n.id === nid)?.label || nid)
        .join(', ');
      setError(
        `Reopen approved downstream work before replacing this image: ${locked}.`
      );
      return;
    }
    const meta = projectImages.find((img) => img.id === imageId);
    updateNode(id, {
      file_id: imageId,
      image: '',
      original_filename: meta?.original_filename || meta?.name || '',
    });
    setReplaceMenuNodeId('');
    setNotice('Attached existing project image. Shared file was not deleted.');
    refreshExecutionPlan();
  };

  const refreshExecutionPlan = useCallback(async () => {
    if (!projectId || !recordId || dirty) {
      setExecutionPlan(null);
      return null;
    }
    setPlanLoading(true);
    try {
      const plan = await bakingApi.getWorkflowExecutionPlan(projectId, recordId);
      setExecutionPlan(plan);
      return plan;
    } catch (_e) {
      setExecutionPlan(null);
      return null;
    } finally {
      setPlanLoading(false);
    }
  }, [projectId, recordId, dirty]);

  useEffect(() => {
    if (!record || dirty) return undefined;
    refreshExecutionPlan();
    const listFn = bakingApi.listProjectImages;
    if (typeof listFn === 'function') {
      Promise.resolve(listFn(projectId))
        .then((data) => {
          const list = Array.isArray(data) ? data : data?.results || [];
          setProjectImages(list.filter((img) => img.status === 'ready' || !img.status));
        })
        .catch(() => {});
    }
    return undefined;
  }, [record, dirty, projectId, refreshExecutionPlan]);

  useEffect(() => {
    if (!record || dirty) return undefined;
    const executable = (record.graph?.nodes || []).filter((n) => isExecutableNode(n.type));
    let cancelled = false;
    executable.forEach(async (node) => {
      try {
        if (typeof bakingApi.getWorkflowNodeEstimate !== 'function') {
          if (!cancelled) {
            setNodeEstimates((prev) => ({ ...prev, [node.id]: localNodeEstimate(node) }));
          }
          return;
        }
        const data = await bakingApi.getWorkflowNodeEstimate(projectId, recordId, node.id);
        if (!cancelled) {
          setNodeEstimates((prev) => ({ ...prev, [node.id]: data.estimate || localNodeEstimate(node) }));
        }
      } catch (_e) {
        if (!cancelled) {
          setNodeEstimates((prev) => ({ ...prev, [node.id]: localNodeEstimate(node) }));
        }
      }
    });
    return () => {
      cancelled = true;
    };
  }, [record, dirty, projectId, recordId, runsByNode]);

  const runAll = async () => {
    if (!canEdit || runAllBusy || runningNodeId || dirty) {
      if (dirty) setError('Save the workflow before Run all.');
      return;
    }
    setRunAllBusy(true);
    setError('');
    try {
      const plan = await refreshExecutionPlan();
      if (!plan?.summary?.run_count) {
        setNotice(plan ? 'No nodes are eligible to run right now.' : 'Could not load execution plan.');
        return;
      }
      if (!runAllIdempotency.current) {
        runAllIdempotency.current = `run-all-${recordId}-${Date.now()}`;
      }
      const result = await bakingApi.runWorkflowExecutionPlan(projectId, recordId, {
        idempotency_key: runAllIdempotency.current,
      });
      setExecutionPlan(result.plan || plan);
      setNotice(result.message || `Queued ${result.queued_runs?.length || 0} run(s).`);
      runAllIdempotency.current = '';
      await loadRuns();
    } catch (e) {
      setError(e.message || 'Run all failed.');
    } finally {
      setRunAllBusy(false);
    }
  };

  const runNode = async (nodeId) => {
    if (!canEdit || runningNodeId) return;
    const target = graph.nodes.find((n) => n.id === nodeId);
    if (target?.type === 'dev_worker' && !canUseDevWorker) {
      setError('Only platform administrators can run the Worker Dev Node.');
      return;
    }
    if (dirty) {
      setError('Save the workflow before running a node so the worker receives the latest graph.');
      return;
    }
    setRunningNodeId(nodeId);
    setError('');
    try {
      const body =
        target?.type === 'dev_worker'
          ? { idempotency_key: `dev-${nodeId}-${Date.now()}` }
          : {};
      const run = await bakingApi.createProductionNodeRun(projectId, recordId, nodeId, body);
      setRunsByNode((prev) => {
        const history = [run, ...(prev[nodeId]?.history || [])];
        return {
          ...prev,
          [nodeId]: {
            ...(prev[nodeId] || {}),
            active_run: run,
            latest_attempt: run,
            history,
            latest_success: prev[nodeId]?.latest_success || null,
          },
        };
      });
      setNotice(`${statusLabel(run.status)} · waiting for worker`);
      await loadRuns();
    } catch (e) {
      setError(e.message || 'Could not start node run.');
    } finally {
      setRunningNodeId('');
    }
  };

  if (!record) {
    return (
      <div>
        <Link to={`/console/projects/${projectId}/departments/${department}`}>← Department</Link>
        {error ? <p role="alert">{error}</p> : <p role="status">Loading workflow…</p>}
      </div>
    );
  }

  const width = Math.max(1250, ...graph.nodes.map((n) => n.x + 340));
  const height = Math.max(650, ...graph.nodes.map((n) => n.y + 320));
  const preview = selected ? assembledText(graph, selected.id, records, runsByNode) : '';
  const previewMaterials = selected
    ? assembledMaterials(graph, selected.id, records, runsByNode)
    : [];
  const connectedMaterials =
    selected && isExecutableNode(selected.type)
      ? connectedInputMaterials(graph, selected.id, records, runsByNode)
      : [];
  const connectedPreview =
    selected && isExecutableNode(selected.type)
      ? connectedInputText(graph, selected.id, records, runsByNode)
      : '';
  const historyMeta = selected ? historyPages[selected.id] : null;
  const showHistoryLoadMore =
    selected &&
    isExecutableNode(selected.type) &&
    Boolean(selectedSummary?.latest_attempt) &&
    !(historyMeta?.scoped && historyMeta?.hasMore === false);
  const picker = PICKER_NODE_TYPES.filter((def) => {
    if (def.adminOnly && !canUseDevWorker) return false;
    const q = pickerQuery.trim().toLowerCase();
    if (!q) return true;
    return (
      def.label.toLowerCase().includes(q) ||
      def.category.includes(q) ||
      def.description.toLowerCase().includes(q)
    );
  });

  return (
    <div className="production-workflow">
      <Link to={`/console/projects/${projectId}/departments/${department}`}>
        ← {department === 'character' ? 'Character' : 'Story'} Department
      </Link>
      <header className="production-header">
        <div>
          <p className="production-eyebrow">{department.toUpperCase()} WORKFLOW</p>
          <h1>{record.name}</h1>
          <p>
            {record.role || 'In development'}{' '}
            <span className="workflow-save-state" role="status">
              · {dirty ? 'Unsaved changes' : notice || 'Saved'}
            </span>
          </p>
        </div>
        {canEdit && (
          <div className="d-flex gap-2">
            {department === 'character' ? (
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={() => setTutorialOpen(true)}
              >
                First character tutorial
              </button>
            ) : null}
            <Link
              className="btn btn-outline-secondary"
              to={`/console/projects/${projectId}/departments/${department}/${recordId}/studio`}
            >
              Open Studio
            </Link>
            <button
              className="btn btn-dark"
              disabled={busy || !dirty || !record.name.trim()}
              onClick={save}
            >
              {busy ? 'Working…' : 'Save workflow'}
            </button>
          </div>
        )}
      </header>
      {error && (
        <div role="alert" className="alert alert-danger">
          {error}
        </div>
      )}

      <div className="workflow-toolbar">
        <div className="workflow-picker">
          <label className="visually-hidden" htmlFor="node-picker-search">
            Search nodes
          </label>
          <input
            id="node-picker-search"
            className="form-control form-control-sm"
            placeholder="Search nodes…"
            value={pickerQuery}
            onChange={(e) => setPickerQuery(e.target.value)}
            disabled={!canEdit || busy}
          />
          <div className="workflow-tools" aria-label="Add node">
            {NODE_CATEGORIES.map((category) => {
              const items = picker.filter((def) => def.category === category.id);
              if (!items.length) return null;
              return (
                <div key={category.id} className="workflow-tool-group">
                  <span>{category.label}</span>
                  {items.map((def) => (
                    <button
                      key={def.type}
                      type="button"
                      disabled={!canEdit || busy || graph.nodes.length >= 100}
                      onClick={() => add(def.type)}
                    >
                      + {def.label}
                    </button>
                  ))}
                </div>
              );
            })}
          </div>
        </div>
        <div className="workflow-zoom">
          {canEdit ? (
            <div className="workflow-run-all">
              <button
                type="button"
                className="btn btn-sm btn-dark"
                disabled={
                  busy ||
                  runAllBusy ||
                  dirty ||
                  planLoading ||
                  Boolean(runningNodeId) ||
                  !(executionPlan?.summary?.run_count > 0)
                }
                title={
                  dirty
                    ? 'Save before Run all'
                    : executionPlan?.summary?.run_count
                      ? `Run ${executionPlan.summary.run_count} eligible node(s)`
                      : 'No eligible nodes in the current plan'
                }
                onClick={runAll}
              >
                {runAllBusy
                  ? 'Running…'
                  : planLoading
                    ? 'Planning…'
                    : `Run all${
                        executionPlan?.summary?.run_count
                          ? ` (${executionPlan.summary.run_count})`
                          : ''
                      }`}
              </button>
              <CostEstimateTooltip
                plan={executionPlan}
                label="Predicted usage for Run all"
              />
            </div>
          ) : null}
          <button
            type="button"
            aria-label="Zoom out"
            disabled={zoom <= 0.5}
            onClick={() => setZoom((z) => z - 0.1)}
          >
            −
          </button>
          <span>{Math.round(zoom * 100)}%</span>
          <button
            type="button"
            aria-label="Zoom in"
            disabled={zoom >= 1.4}
            onClick={() => setZoom((z) => z + 0.1)}
          >
            +
          </button>
        </div>
      </div>

      {executionPlan && canEdit ? (
        <div className="workflow-plan-summary" role="status">
          Run all plan · run {executionPlan.summary?.run_count || 0} · skip{' '}
          {executionPlan.summary?.skip_count || 0} · blocked{' '}
          {executionPlan.summary?.blocked_count || 0}
          {executionPlan.summary?.awaiting_review_count
            ? ` · awaiting review ${executionPlan.summary.awaiting_review_count}`
            : ''}
          {hasActiveRuns ? ' · in progress…' : ''}
        </div>
      ) : null}

      <div className="workflow-guidance" role="status">
        {source ? (
          <>
            Connect “{graph.nodes.find((n) => n.id === source.nodeId)?.label}”
            {source.portId ? ` · ${source.portId}` : ''} to a compatible input.{' '}
            <button className="btn btn-sm btn-outline-secondary" type="button" onClick={() => setSource(null)}>
              Cancel connection
            </button>
          </>
        ) : (
          'Right-click the canvas or use Add node. Drag headers to arrange; use labeled ports to connect. Creating or connecting a node never runs it.'
        )}
        {usageTotals && (
          <div className="workflow-totals" aria-label="Workflow usage totals">
            Workflow totals · {formatWorkflowTotals(usageTotals)}
          </div>
        )}
      </div>

      <div className="workflow-layout">
        <div className="workflow-scroll" aria-label="Workflow canvas" tabIndex={0}
          onContextMenu={(e) => {
            if (!canEdit || busy) return;
            e.preventDefault();
            const rect = e.currentTarget.getBoundingClientRect();
            const x = Math.max(20, (e.clientX - rect.left) / zoom);
            const y = Math.max(20, (e.clientY - rect.top + e.currentTarget.scrollTop) / zoom);
            setContextMenu({ x, y, clientX: e.clientX, clientY: e.clientY, query: '' });
          }}
        >
          <div style={{ width: width * zoom, height: height * zoom }}>
            <div
              className="workflow-canvas"
              style={{ width, height, transform: `scale(${zoom})`, transformOrigin: 'top left' }}
            >
              <svg className="workflow-wires" width={width} height={height} aria-hidden="true">
                <defs>
                  <marker id="flow-arrow" markerWidth="8" markerHeight="8" refX="7" refY="4" orient="auto">
                    <path d="M0,0 L8,4 L0,8" fill="#7781a3" />
                  </marker>
                </defs>
                {graph.edges.map((edge) => {
                  const a = graph.nodes.find((n) => n.id === edge.source);
                  const b = graph.nodes.find((n) => n.id === edge.target);
                  if (!a || !b) return null;
                  const x = a.x + 244;
                  const y = a.y + 86;
                  const tx = b.x - 5;
                  const ty = b.y + 86;
                  return (
                    <path
                      key={`${edge.source}:${edge.source_port || 'out'}-${edge.target}:${edge.target_port || 'in'}`}
                      d={`M ${x},${y} C ${x + 65},${y} ${tx - 65},${ty} ${tx},${ty}`}
                      fill="none"
                      stroke="#7781a3"
                      strokeWidth="2"
                      markerEnd="url(#flow-arrow)"
                    />
                  );
                })}
              </svg>
              {graph.nodes.map((node) => {
                const def = getNodeDef(node.type);
                const ports = defaultPortsForType(node.type);
                const summary = runsByNode[node.id];
                const state = nodeExecutionState(summary);
                const retained = retainedSuccessPreview(summary);
                const previewRun = retained?.run;
                const latestAttempt = summary?.latest_attempt || summary?.history?.[0] || summary?.active_run;
                const usage = latestAttempt?.usage || previewRun?.usage;
                const wallMs = latestAttempt?.duration_ms;
                const execGate = canExecuteCatalogNode(node, graph);
                const availability = def.availability || 'available';
                return (
                  <article
                    key={node.id}
                    className={`workflow-node node-${node.type} ${node.id === selectedId ? 'selected' : ''} ${isExecutableNode(node.type) ? 'executable' : ''} ${node.approved ? 'approved' : ''}`}
                    style={{ left: node.x, top: node.y }}
                    onClick={() => setSelectedId(node.id)}
                  >
                    <button
                      type="button"
                      className="workflow-node-header"
                      aria-label={`Select and move ${node.label}`}
                      onPointerDown={(e) => {
                        setSelectedId(node.id);
                        if (!canEdit || busy) return;
                        drag.current = {
                          id: node.id,
                          startX: e.clientX,
                          startY: e.clientY,
                          x: node.x,
                          y: node.y,
                        };
                        e.currentTarget.setPointerCapture?.(e.pointerId);
                      }}
                      onPointerMove={(e) => {
                        const d = drag.current;
                        if (
                          d?.id === node.id &&
                          Math.abs(e.clientX - d.startX) + Math.abs(e.clientY - d.startY) > 3
                        ) {
                          updateNode(node.id, {
                            x: Math.min(10000, Math.max(20, d.x + (e.clientX - d.startX) / zoom)),
                            y: Math.min(10000, Math.max(20, d.y + (e.clientY - d.startY) / zoom)),
                          });
                        }
                      }}
                      onPointerUp={() => {
                        drag.current = null;
                      }}
                      onPointerCancel={() => {
                        drag.current = null;
                      }}
                    >
                      <span>{def.label}</span>
                      <span aria-hidden="true">⠿</span>
                    </button>
                    <div className="workflow-ports-in" aria-label={`Inputs for ${node.label}`}>
                      {(ports.inputs || []).map((port) => {
                        const connected = graph.edges.some(
                          (e) => e.target === node.id && (e.target_port || 'in') === port.id
                        );
                        const missing = port.required && !connected;
                        return (
                          <button
                            key={port.id}
                            type="button"
                            className={`workflow-port named input ${connected ? 'connected' : ''} ${missing ? 'missing' : ''} ${!port.required && !connected ? 'optional-empty' : ''}`}
                            style={{ '--port-color': PORT_TYPES[port.type]?.color || '#8c8b96' }}
                            aria-label={`${port.label} input (${PORT_TYPES[port.type]?.label || port.type})${port.required ? ', required' : ''}`}
                            title={`${port.label} · ${PORT_TYPES[port.type]?.label || port.type}${port.required ? ' · required' : ''}`}
                            disabled={!source || !canEdit || busy}
                            onClick={(e) => {
                              e.stopPropagation();
                              join(node.id, port.id);
                            }}
                          >
                            <span className="workflow-port-dot" aria-hidden="true" />
                            <span className="workflow-port-label">{port.label}{port.required ? ' *' : ''}</span>
                          </button>
                        );
                      })}
                    </div>
                    <h3>{node.label}</h3>
                    {availability === 'worker_mock' ? (
                      <p className="workflow-capability-badge mock" role="status">Worker mock result</p>
                    ) : null}
                    {availability === 'unavailable' ? (
                      <p className="workflow-capability-badge unavailable" role="status">Unavailable</p>
                    ) : null}
                    {node.approved ? (
                      <p className="workflow-capability-badge approved" role="status">Approved</p>
                    ) : null}
                    {isExecutableNode(node.type) ? (
                      <div className="workflow-node-body">
                        <p className="workflow-node-instructions">
                          {(node.text || 'Add instructions in the inspector.').slice(0, 90)}
                        </p>
                        <div className={`workflow-run-status status-${state}`}>
                          {statusLabel(state)}
                          {latestAttempt?.error_message ? ` · ${latestAttempt.error_message.slice(0, 48)}` : ''}
                          {previewRun?.result_stale ? ' · earlier inputs' : ''}
                        </div>
                        <div className="workflow-node-result">
                          {retained?.retained && (
                            <p className="workflow-retained-label">Earlier successful result retained</p>
                          )}
                          {previewRun?.result?.image ? (
                            <img src={previewRun.result.image} alt="" />
                          ) : previewRun?.result?.download_url ? (
                            <p className="workflow-artifact-card">
                              <a href={previewRun.result.download_url} target="_blank" rel="noreferrer">
                                Open report
                              </a>
                            </p>
                          ) : previewRun?.result?.text ? (
                            <p>{previewRun.result.text.slice(0, 120)}</p>
                          ) : previewRun?.artifacts?.length ? (
                            <p className="workflow-artifact-card">
                              {previewRun.artifacts[0].mock ? 'Mock artifact · ' : ''}
                              {previewRun.artifacts[0].name || 'Artifact'}
                            </p>
                          ) : (
                            <p className="workflow-empty-result">
                              {node.type === 'dev_worker'
                                ? 'No result yet. Run the administrator Dev Node.'
                                : 'No result yet. Connect inputs and Run.'}
                            </p>
                          )}
                        </div>
                        <div className="workflow-node-actions">
                          <div className="workflow-run-row">
                            <button
                              type="button"
                              className="btn btn-sm btn-dark workflow-run-btn"
                              disabled={
                                !canEdit ||
                                busy ||
                                runAllBusy ||
                                Boolean(summary?.active_run) ||
                                runningNodeId === node.id ||
                                Boolean(runningNodeId) ||
                                !execGate.ok ||
                                dirty ||
                                (node.type === 'dev_worker' && !canUseDevWorker)
                              }
                              title={
                                dirty
                                  ? 'Save the workflow before running.'
                                  : execGate.ok
                                    ? 'Run this node with current settings and connected inputs'
                                    : execGate.reason
                              }
                              onClick={(e) => {
                                e.stopPropagation();
                                runNode(node.id);
                              }}
                              onPointerDown={(e) => e.stopPropagation()}
                            >
                              {summary?.active_run
                                ? statusLabel(summary.active_run.status)
                                : runningNodeId === node.id
                                  ? 'Starting…'
                                  : !execGate.ok
                                    ? 'Cannot run'
                                    : dirty
                                      ? 'Save to run'
                                      : state === 'failed' || state === 'timed_out'
                                        ? 'Retry'
                                        : 'Run'}
                            </button>
                            <CostEstimateTooltip
                              estimate={nodeEstimates[node.id] || localNodeEstimate(node)}
                              label={`Predicted usage for ${node.label}`}
                            />
                          </div>
                          {!execGate.ok ? (
                            <p className="workflow-run-reason" role="status">
                              {execGate.reason}
                            </p>
                          ) : null}
                          <span className="workflow-usage-chip">
                            {usage
                              ? `Actual · ${formatUsageSummary(usage, { durationMs: wallMs })}`
                              : 'Actual usage appears after a completed run.'}
                          </span>
                        </div>
                      </div>
                    ) : node.type === 'image' ? (
                      <div className="workflow-image-body">
                        {node.image ? (
                          <img src={node.image} alt={node.label} />
                        ) : node.file_id ? (
                          <p className="workflow-empty-result">
                            {(node.original_filename || 'Project image').slice(0, 48)}
                          </p>
                        ) : (
                          <p className="workflow-empty-result">No image yet</p>
                        )}
                        <p className="workflow-node-instructions">
                          {(imageNodePrompt(node) || 'Optional instructions').slice(0, 90)}
                        </p>
                        {replaceProgress[node.id] != null ? (
                          <p className="workflow-run-reason" role="status">
                            Uploading… {replaceProgress[node.id]}%
                          </p>
                        ) : null}
                        <div className="workflow-node-actions">
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            disabled={!canEdit || busy}
                            title={
                              imageInputLocked(graph, node.id)
                                ? 'Reopen approved downstream work before replacing'
                                : 'Replace this image input'
                            }
                            onClick={(e) => {
                              e.stopPropagation();
                              if (imageInputLocked(graph, node.id)) {
                                const locked = downstreamApprovalsInvalidated(graph, [node.id])
                                  .map((nid) => graph.nodes.find((n) => n.id === nid)?.label || nid)
                                  .join(', ');
                                setError(
                                  `Reopen approved downstream work before replacing this image: ${locked}.`
                                );
                                return;
                              }
                              setSelectedId(node.id);
                              setReplaceMenuNodeId(
                                replaceMenuNodeId === node.id ? '' : node.id
                              );
                            }}
                            onPointerDown={(e) => e.stopPropagation()}
                          >
                            Replace
                          </button>
                          {replaceMenuNodeId === node.id ? (
                            <div
                              className="workflow-replace-menu"
                              onPointerDown={(e) => e.stopPropagation()}
                            >
                              <label className="btn btn-sm btn-outline-secondary">
                                Upload new
                                <input
                                  type="file"
                                  accept={ACCEPTED_IMAGE_TYPES.join(',')}
                                  hidden
                                  onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (node.file_id) replaceExistingFile(node.id, file);
                                    else upload(file, node.id);
                                  }}
                                />
                              </label>
                              <label>
                                Existing project image
                                <select
                                  className="form-select form-select-sm"
                                  defaultValue=""
                                  onChange={(e) => attachProjectImage(node.id, e.target.value)}
                                >
                                  <option value="">Choose…</option>
                                  {projectImages.map((img) => (
                                    <option key={img.id} value={img.id}>
                                      {img.original_filename || img.name || img.id}
                                    </option>
                                  ))}
                                </select>
                              </label>
                            </div>
                          ) : null}
                        </div>
                      </div>
                    ) : node.image ? (
                      <img src={node.image} alt={node.label} />
                    ) : (
                      <p>
                        {node.type === 'action'
                          ? 'Legacy planned action — use Worker / Create Images / Create 3D'
                          : node.type === 'reference'
                            ? records.find((r) => r.id === node.reference)?.name || 'Choose shared material'
                            : node.text ||
                              (node.type === 'combine'
                                ? 'Bring upstream material together'
                                : node.type === 'output'
                                  ? 'Your endpoint'
                                  : 'Add your material')}
                      </p>
                    )}
                    <span className="workflow-node-footer">
                      {isExecutableNode(node.type)
                        ? 'PROCESSING'
                        : node.type === 'output' || node.type === 'export_character'
                          ? 'ENDPOINT'
                          : node.type === 'combine'
                            ? 'UTILITY'
                            : 'WORKING MATERIAL'}
                    </span>
                    <div className="workflow-ports-out" aria-label={`Outputs for ${node.label}`}>
                      {(ports.outputs || []).map((port) => {
                        const connected = graph.edges.some(
                          (e) => e.source === node.id && (e.source_port || 'out') === port.id
                        );
                        const active =
                          source?.nodeId === node.id && (source.portId || 'out') === port.id;
                        return (
                          <button
                            key={port.id}
                            type="button"
                            className={`workflow-port named output ${connected ? 'connected' : ''} ${active ? 'active' : ''}`}
                            style={{ '--port-color': PORT_TYPES[port.type]?.color || '#8c8b96' }}
                            aria-label={`${port.label} output (${PORT_TYPES[port.type]?.label || port.type})`}
                            title={`${port.label} · ${PORT_TYPES[port.type]?.label || port.type}`}
                            disabled={!canEdit || busy}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSource({ nodeId: node.id, portId: port.id });
                            }}
                          >
                            <span className="workflow-port-label">{port.label}</span>
                            <span className="workflow-port-dot" aria-hidden="true" />
                          </button>
                        );
                      })}
                    </div>
                  </article>
                );
              })}
              {!graph.nodes.length && (
                <div className="production-empty">
                  <h2>A blank canvas.</h2>
                  <p>Add written material or an image to begin.</p>
                </div>
              )}
            </div>
          </div>
        </div>

        <aside className="workflow-inspector" aria-label="Node details">
          <h2 className="h5">{selected ? getNodeDef(selected.type).label : 'Workflow details'}</h2>
          <fieldset disabled={!canEdit || busy}>
            <label>
              Record name
              <input
                className="form-control"
                value={record.name}
                maxLength={200}
                onChange={(e) => change({ name: e.target.value })}
              />
            </label>
            <label>
              Role / arc
              <input
                className="form-control"
                value={record.role}
                maxLength={200}
                onChange={(e) => change({ role: e.target.value })}
              />
            </label>
            <label>
              Shared brief
              <textarea
                className="form-control"
                rows={3}
                maxLength={10000}
                value={record.brief}
                onChange={(e) => change({ brief: e.target.value })}
              />
            </label>
            <p className="small text-muted">Other departments reference this record’s name and shared brief.</p>
            {selected && (
              <>
                <hr />
                <label>
                  Node label
                  <input
                    className="form-control"
                    value={selected.label}
                    maxLength={200}
                    onChange={(e) => updateNode(selected.id, { label: e.target.value })}
                  />
                </label>
                {selected.type !== 'image' && (
                <label>
                  {selected.type === 'dev_worker'
                    ? 'Notes (not used as worker instructions)'
                    : isExecutableNode(selected.type)
                      ? 'Instructions'
                      : 'Written material / notes'}
                  <textarea
                    className="form-control"
                    rows={5}
                    maxLength={20000}
                    value={selected.text || ''}
                    onChange={(e) => updateNode(selected.id, { text: e.target.value })}
                  />
                </label>
                )}
                {selected.type === 'dev_worker' && (
                  <>
                    <p className="small text-muted">
                      Platform administrators only. Trusted task instructions are embedded server-side;
                      this node runs the deterministic <code>dev_test</code> operation.
                    </p>
                    <label>
                      Structured info (JSON object)
                      <textarea
                        className="form-control"
                        rows={4}
                        value={
                          typeof selected.info === 'string'
                            ? selected.info
                            : JSON.stringify(selected.info || { note: 'dev probe' }, null, 2)
                        }
                        onChange={(e) => {
                          const raw = e.target.value;
                          try {
                            const parsed = JSON.parse(raw);
                            if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
                              updateNode(selected.id, { info: parsed });
                              return;
                            }
                          } catch (_err) {
                            /* keep typing */
                          }
                          updateNode(selected.id, { info: raw });
                        }}
                      />
                    </label>
                    <label>
                      Attachment URLs (one per line)
                      <textarea
                        className="form-control"
                        rows={3}
                        placeholder="https://…"
                        value={
                          Array.isArray(selected.attachment_urls)
                            ? selected.attachment_urls.join('\n')
                            : selected.attachment_urls || ''
                        }
                        onChange={(e) =>
                          updateNode(selected.id, {
                            attachment_urls: e.target.value
                              .split('\n')
                              .map((line) => line.trim())
                              .filter(Boolean),
                          })
                        }
                      />
                    </label>
                  </>
                )}
                {selected.type === 'image' && (
                  <>
                    <label>
                      Prompt
                      <textarea
                        className="form-control"
                        rows={4}
                        value={selected.prompt || selected.text || ''}
                        onChange={(e) =>
                          updateNode(selected.id, {
                            prompt: e.target.value,
                            text: e.target.value,
                          })
                        }
                        placeholder="How this image should influence downstream generation"
                      />
                    </label>
                    {imageInputLocked(graph, selected.id) ? (
                      <p className="project-files-hint" role="status">
                        Reopen approved downstream work before replacing:{' '}
                        {downstreamApprovalsInvalidated(graph, [selected.id])
                          .map((id) => graph.nodes.find((n) => n.id === id)?.label || id)
                          .join(', ')}
                      </p>
                    ) : null}
                    <div className="workflow-replace-menu">
                      <strong className="d-block mb-2">Replace image</strong>
                      <label className="btn btn-sm btn-outline-secondary">
                        Upload
                        <input
                          key={`${selected.id}-upload`}
                          type="file"
                          accept={ACCEPTED_IMAGE_TYPES.join(',')}
                          hidden
                          disabled={imageInputLocked(graph, selected.id) || busy}
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (selected.file_id) replaceExistingFile(selected.id, file);
                            else upload(file, selected.id);
                          }}
                        />
                      </label>
                      <label>
                        Existing project image
                        <select
                          className="form-select form-select-sm"
                          value=""
                          disabled={imageInputLocked(graph, selected.id) || busy}
                          onChange={(e) => attachProjectImage(selected.id, e.target.value)}
                        >
                          <option value="">Choose…</option>
                          {projectImages.map((img) => (
                            <option key={img.id} value={img.id}>
                              {img.original_filename || img.name || img.id}
                            </option>
                          ))}
                        </select>
                      </label>
                      {replaceProgress[selected.id] != null ? (
                        <p className="small text-muted">Uploading… {replaceProgress[selected.id]}%</p>
                      ) : null}
                    </div>
                    <p className="small text-muted">
                      PNG, JPEG, or WebP · up to {Math.round(MAX_IMAGE_BYTES / (1024 * 1024))} MB.
                      Replacement keeps node id, ports, and instructions. Shared files are not deleted;
                      historical snapshots stay unchanged.
                    </p>
                    <label>
                      Source / credit
                      <input
                        className="form-control"
                        value={selected.provenance || ''}
                        onChange={(e) => updateNode(selected.id, { provenance: e.target.value })}
                      />
                    </label>
                    {selected.file_id && (
                      <p className="small text-muted">file_id: {selected.file_id}</p>
                    )}
                    {(selected.file_id || selected.image) && (
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        disabled={imageInputLocked(graph, selected.id)}
                        onClick={() => updateNode(selected.id, { file_id: '', image: '' })}
                      >
                        Detach image (keeps stored file)
                      </button>
                    )}
                  </>
                )}
                {selected.type === 'reference' && (
                  <label>
                    Shared department record
                    <select
                      className="form-select"
                      value={selected.reference || ''}
                      onChange={(e) => updateNode(selected.id, { reference: e.target.value })}
                    >
                      <option value="">Choose a record…</option>
                      {records
                        .filter((r) => r.id !== record.id)
                        .map((r) => (
                          <option key={r.id} value={r.id}>
                            {r.department === 'story' ? 'Story' : 'Character'} / {r.name}
                          </option>
                        ))}
                    </select>
                  </label>
                )}
                {selected.type === 'action' && (
                  <>
                    <label>
                      Action
                      <select
                        className="form-select"
                        value={selected.action || 'refine_text'}
                        onChange={(e) => updateNode(selected.id, { action: e.target.value })}
                      >
                        <option value="refine_text">Refine description</option>
                        <option value="generate_image">Generate reference image</option>
                        <option value="generate_model">Generate 3D model</option>
                      </select>
                    </label>
                    <p className="workflow-pending">
                      Legacy planned action. Add a Worker, Create Images, or Create 3D node to execute.
                    </p>
                  </>
                )}
                {isExecutableNode(selected.type) &&
                  (getNodeDef(selected.type).settings || []).map((field) => (
                    <label key={field.key}>
                      {field.label}
                      <input
                        className="form-control"
                        value={(selected.settings && selected.settings[field.key]) || field.default || ''}
                        onChange={(e) =>
                          updateNode(selected.id, {
                            settings: { ...(selected.settings || {}), [field.key]: e.target.value },
                          })
                        }
                      />
                    </label>
                  ))}
                {selected.type === 'output' && (
                  <label>
                    Output type
                    <select
                      className="form-select"
                      value={selected.format || 'description'}
                      onChange={(e) => updateNode(selected.id, { format: e.target.value })}
                    >
                      <option value="description">Written description</option>
                      <option value="model">3D model (planned)</option>
                    </select>
                  </label>
                )}
                <div className="workflow-position">
                  <label>
                    X
                    <input
                      type="number"
                      className="form-control"
                      min="0"
                      max="10000"
                      value={Math.round(selected.x)}
                      onChange={(e) =>
                        updateNode(selected.id, {
                          x: Math.max(0, Math.min(10000, Number(e.target.value))),
                        })
                      }
                    />
                  </label>
                  <label>
                    Y
                    <input
                      type="number"
                      className="form-control"
                      min="0"
                      max="10000"
                      value={Math.round(selected.y)}
                      onChange={(e) =>
                        updateNode(selected.id, {
                          y: Math.max(0, Math.min(10000, Number(e.target.value))),
                        })
                      }
                    />
                  </label>
                </div>
                <h3 className="h6 mt-3">Connections</h3>
                {graph.edges
                  .filter((e) => e.source === selected.id || e.target === selected.id)
                  .map((edge) => (
                    <div className="workflow-connection" key={`${edge.source}-${edge.target}`}>
                      <span>
                        {graph.nodes.find((n) => n.id === edge.source)?.label} →{' '}
                        {graph.nodes.find((n) => n.id === edge.target)?.label}
                      </span>
                      <button
                        type="button"
                        aria-label="Remove connection"
                        onClick={() =>
                          change({
                            graph: {
                              ...graph,
                              edges: graph.edges.filter((e) => e !== edge),
                            },
                          })
                        }
                      >
                        ×
                      </button>
                    </div>
                  ))}
                <label>
                  Connect from
                  <select
                    className="form-select"
                    value={source?.nodeId || ''}
                    onChange={(e) =>
                      setSource(e.target.value ? { nodeId: e.target.value, portId: 'out' } : null)
                    }
                  >
                    <option value="">Choose upstream node…</option>
                    {graph.nodes
                      .filter((n) => n.type !== 'output' && n.type !== 'export_character' && n.id !== selected.id)
                      .map((n) => (
                        <option value={n.id} key={n.id}>
                          {n.label}
                        </option>
                      ))}
                  </select>
                </label>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  disabled={!source}
                  onClick={() => join(selected.id)}
                >
                  Connect to this node
                </button>
                {isExecutableNode(selected.type) && (
                  <>
                    {(() => {
                      const gate = canExecuteCatalogNode(selected, graph);
                      return (
                        <>
                          {!gate.ok ? (
                            <p className="project-files-hint" role="status">{gate.reason}</p>
                          ) : null}
                          {gate.mock ? (
                            <p className="workflow-capability-badge mock" role="status">
                              This run uses a labeled worker mock. Results are not production art.
                            </p>
                          ) : null}
                          <div className="workflow-run-row mt-2">
                            <button
                              type="button"
                              className="btn btn-sm btn-dark"
                              disabled={
                                !gate.ok ||
                                dirty ||
                                Boolean(selectedSummary?.active_run) ||
                                runningNodeId === selected.id ||
                                Boolean(runningNodeId) ||
                                runAllBusy ||
                                (selected.type === 'dev_worker' && !canUseDevWorker)
                              }
                              title={
                                dirty
                                  ? 'Save before running'
                                  : gate.ok
                                    ? ''
                                    : gate.reason
                              }
                              onClick={() => runNode(selected.id)}
                            >
                              Run this node
                            </button>
                            <CostEstimateTooltip
                              estimate={
                                nodeEstimates[selected.id] || localNodeEstimate(selected)
                              }
                              label={`Predicted usage for ${selected.label}`}
                            />
                          </div>
                          {selectedSummary?.latest_success ? (
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary mt-2 d-block"
                              onClick={() => {
                                if (selected.approved) {
                                  const affected = downstreamApprovalsInvalidated(graph, [
                                    selected.id,
                                  ]);
                                  const labels = affected
                                    .map((id) => graph.nodes.find((n) => n.id === id)?.label || id)
                                    .join(', ');
                                  if (
                                    affected.length &&
                                    !window.confirm(
                                      `Reopening will mark these downstream approvals for review: ${labels}. Continue?`
                                    )
                                  ) {
                                    return;
                                  }
                                }
                                updateNode(selected.id, {
                                  approved: !selected.approved,
                                  selected_run_id: selected.approved
                                    ? ''
                                    : selectedSummary.latest_success.id,
                                });
                              }}
                            >
                              {selected.approved ? 'Reopen approval' : 'Approve latest result'}
                            </button>
                          ) : null}
                        </>
                      );
                    })()}
                  </>
                )}
                {selected.type === 'export_character' && (
                  <div className="mt-3">
                    <p className="small text-muted">
                      Builds <code>bakeonator_package_v1</code> from approved upstream run versions.
                      OpenUSD Character Stage is a separate demo — not produced here.
                    </p>
                    {selected.last_export?.package_id ? (
                      <p className="workflow-capability-badge approved" role="status">
                        Last package · {selected.last_export.package_id.slice(0, 8)}…
                        {selected.last_export.download_url ? (
                          <>
                            {' '}
                            <a href={selected.last_export.download_url} target="_blank" rel="noreferrer">
                              Open package
                            </a>
                          </>
                        ) : null}
                      </p>
                    ) : null}
                    <button
                      type="button"
                      className="btn btn-sm btn-dark d-block"
                      disabled={!canEdit || busy || dirty}
                      title={dirty ? 'Save the workflow before exporting.' : ''}
                      onClick={async () => {
                        setBusy(true);
                        setError('');
                        try {
                          const res = await bakingApi.exportCharacterPackage(
                            projectId,
                            recordId,
                            selected.id,
                            { format: 'bakeonator_package_v1' }
                          );
                          if (res.record) setRecord(res.record);
                          setDirty(false);
                          setNotice(
                            `Export package ${res.export?.package_id || ''} saved (${res.export?.format}).`
                          );
                        } catch (e) {
                          setError(e.message || 'Export failed.');
                        } finally {
                          setBusy(false);
                        }
                      }}
                    >
                      Build export package
                    </button>
                  </div>
                )}
                <button
                  type="button"
                  className="btn btn-sm btn-outline-danger mt-3 d-block"
                  onClick={() => {
                    change({
                      graph: {
                        nodes: graph.nodes.filter((n) => n.id !== selected.id),
                        edges: graph.edges.filter(
                          (e) => e.source !== selected.id && e.target !== selected.id
                        ),
                      },
                    });
                    setSelectedId(null);
                    setSource(null);
                  }}
                >
                  Delete node
                </button>
              </>
            )}
          </fieldset>
          {selected?.type === 'reference' &&
            selected.reference &&
            records.some((r) => r.id === selected.reference) && (
              <Link
                className="d-block mt-3"
                to={`/console/projects/${projectId}/departments/${
                  records.find((r) => r.id === selected.reference).department
                }/${selected.reference}`}
              >
                Open referenced record →
              </Link>
            )}
          {selected && ['combine', 'output'].includes(selected.type) && (
            <section className="workflow-preview">
              <h3 className="h6">Assembled material</h3>
              <p className="small text-muted">
                Same materials the worker receives: writing, image references, artifacts, and
                readiness.
              </p>
              {selected.format === 'model' && (
                <p className="workflow-pending">3D model generation uses Create 3D processing nodes.</p>
              )}
              {previewMaterials.some((m) => m.image) && (
                <div className="workflow-preview-images">
                  {previewMaterials
                    .filter((m) => m.image)
                    .map((m) => (
                      <figure key={`${m.nodeId}-img`}>
                        <img src={m.image} alt={m.label || 'Reference'} />
                        <figcaption>{m.label}</figcaption>
                      </figure>
                    ))}
                </div>
              )}
              <pre>{preview || 'Connect some upstream material to see it here.'}</pre>
              {previewMaterials.some((m) => m.ready === false) && (
                <p className="workflow-pending">Some upstream processing results are missing or outdated.</p>
              )}
              {preview && (
                <a
                  className="btn btn-sm btn-outline-secondary"
                  download={`${record.name.replace(/[^a-z0-9_-]/gi, '_')}-material.txt`}
                  href={`data:text/plain;charset=utf-8,${encodeURIComponent(preview)}`}
                >
                  Download text
                </a>
              )}
            </section>
          )}
          {selected && isExecutableNode(selected.type) && (
            <section className="workflow-preview">
              <h3 className="h6">Connected inputs</h3>
              <p className="small text-muted">
                Exact materials this Run will send (separate from instructions and generated output).
              </p>
              {selected.text ? (
                <p className="workflow-instructions-chip">
                  Instructions · {(selected.text || '').slice(0, 160)}
                  {(selected.text || '').length > 160 ? '…' : ''}
                </p>
              ) : (
                <p className="workflow-pending">Add execution instructions on this node before running.</p>
              )}
              {connectedMaterials.some((m) => m.image) && (
                <div className="workflow-preview-images">
                  {connectedMaterials
                    .filter((m) => m.image)
                    .map((m) => (
                      <figure key={`${m.nodeId}-in`}>
                        <img src={m.image} alt={m.label || 'Reference'} />
                        <figcaption>{m.label}</figcaption>
                      </figure>
                    ))}
                </div>
              )}
              <pre>{connectedPreview || 'Connect upstream material to see it here before the first run.'}</pre>
              {connectedMaterials.some((m) => m.ready === false) && (
                <p className="workflow-pending">Some upstream processing results are missing or outdated.</p>
              )}
            </section>
          )}
          {selected && isExecutableNode(selected.type) && (
            <section className="workflow-preview">
              <h3 className="h6">Run history</h3>
              <p className="small text-muted">
                Before run: Estimate unavailable. After run: usage basis is labeled measured,
                estimated, simulated, or unavailable. Mock runs never invent a provider charge.
              </p>
              {(selectedSummary?.history || []).length === 0 && !selectedSummary?.latest_attempt && (
                <p className="small text-muted">No runs yet.</p>
              )}
              {(selectedSummary?.history || []).length === 0 && selectedSummary?.latest_attempt && (
                <p className="small text-muted">
                  This node has older runs outside the first workflow page. Load history to view them.
                </p>
              )}
              {(selectedSummary?.history || []).map((run) => (
                <div key={run.id} className="workflow-run-card">
                  <strong>
                    #{run.attempt_number} · {statusLabel(run.status)}
                  </strong>
                  <div className="small text-muted">
                    {run.finished_at
                      ? new Date(run.finished_at).toLocaleString()
                      : run.created_at
                        ? new Date(run.created_at).toLocaleString()
                        : ''}
                  </div>
                  {run.error_message && <p className="text-danger small mb-1">{run.error_message}</p>}
                  {run.result?.text && <pre>{run.result.text}</pre>}
                  {run.result?.image && <img src={run.result.image} alt="" />}
                  {(run.artifacts || []).map((artifact, index) => (
                    <p key={index} className="workflow-artifact-card">
                      {artifact.mock ? 'Mock · ' : ''}
                      {artifact.name || artifact.kind || 'Artifact'}
                    </p>
                  ))}
                  <p className="workflow-usage-chip">
                    {formatUsageSummary(run.usage, { durationMs: run.duration_ms })}
                  </p>
                  {run.provider?.identity && (
                    <p className="small text-muted">
                      Provider: {run.provider.identity}
                      {run.provider.mock ? ' (mock)' : ''}
                    </p>
                  )}
                  {run.result_stale && (
                    <p className="workflow-pending">Result is based on earlier inputs.</p>
                  )}
                  {run.status === 'succeeded' && canEdit ? (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary mt-1"
                      onClick={() =>
                        updateNode(selected.id, {
                          approved: true,
                          selected_run_id: run.id,
                        })
                      }
                    >
                      {selected.selected_run_id === run.id && selected.approved
                        ? 'Pinned for downstream'
                        : 'Approve & pin this result'}
                    </button>
                  ) : null}
                </div>
              ))}
              {showHistoryLoadMore && (
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary mt-2"
                  disabled={historyLoading === selected.id}
                  onClick={() => loadMoreHistory(selected.id)}
                >
                  {historyLoading === selected.id ? 'Loading…' : 'Load more history'}
                </button>
              )}
            </section>
          )}
        </aside>
      </div>

      {contextMenu ? (
        <CatalogNodePicker
          open
          className="workflow-context-menu"
          clientX={contextMenu.clientX}
          clientY={contextMenu.clientY}
          query={contextMenu.query || ''}
          onQueryChange={(q) => setContextMenu({ ...contextMenu, query: q })}
          onPick={(type) => add(type, { x: contextMenu.x, y: contextMenu.y })}
          onClose={() => setContextMenu(null)}
          includeAdmin={canUseDevWorker}
          title="Add node"
        />
      ) : null}

      {tutorialOpen ? (
        <FirstCharacterTutorial
          projectId={projectId}
          recordId={recordId}
          department={department}
          graph={graph}
          runsByNode={runsByNode}
          onClose={() => setTutorialOpen(false)}
          onFocusNode={(id) => setSelectedId(id)}
        />
      ) : null}
    </div>
  );
}
